import React, { useEffect, useState, useMemo } from "react";
import { Menu } from "../../components/menu";
import CateringOrderSummary from "../../components/cateringPackages/CateringOrderSummary";
import PackageDetailFilterBar from "../../components/cateringPackages/PackageDetailFilterBar";
import AdditionalServices from "../../components/cateringPackages/AdditionalServices";
import { useDispatch, useSelector } from "react-redux";
import {
  getRestaurantItemsCategoriesAsync,
  getCateringPackageDetailAsync,
  getCateringPackagesAsync,
} from "../../redux/features/restaurants/restaurantThunks";
import { addCateringCartPackage, addItemToCateringPackage, clearCateringItemsToRestore, setCateringCartFilters, setAdditionalServices, setSelectedAdditionalService, setAdditionalServiceItems, setAdditionalServiceCategories, setCateringAdditionalServiceFilters } from "../../redux/features/restaurants/restaurantSlice";

import {
  selectBranchDetails,
  selectCartItems,
  selectShowProductDetail,
  selectCateringItems,
  selectCateringCategories,
  selectCateringPackages,
  selectCateringPackagesTimestamp,
  selectCateringRestaurantId,
  selectCartRestaurantDetails,
  selectCateringCartFilters,
  selectCateringCartPackages,
  selectCateringItemsToRestore,
  selectIsModifyOrderMode,
  selectModifyOrderPax,
  selectAdditionalServices,
  selectSelectedAdditionalService,
  selectAdditionalServiceItems,
  selectAdditionalServiceCategories,
  selectCateringAdditionalServiceFilters,
} from "../../redux/features/restaurants/restaurantSelectors";

import Product from "../../components/menu/product";
import { useParams, useNavigate } from "react-router-dom";

const PlaceCateringOrder = () => {
  console.log("PlaceCateringOrder component rendered!");
  const { packageId } = useParams(); // Get package ID from URL
  console.log("Package ID from URL:", packageId);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const branchDetails = useSelector(selectBranchDetails);
  const showProductDetail = useSelector(selectShowProductDetail);
  const cateringItems = useSelector(selectCateringItems);
  const cateringCategories = useSelector(selectCateringCategories);
  const cateringPackages = useSelector(selectCateringPackages);
  const cateringPackagesTimestamp = useSelector(selectCateringPackagesTimestamp);
  const cateringRestaurantId = useSelector(selectCateringRestaurantId);
  const cartRestaurantDetails = useSelector(selectCartRestaurantDetails);
  const filters = useSelector(selectCateringCartFilters);
  const cateringCartPackages = useSelector(selectCateringCartPackages);
  const itemsToRestore = useSelector(selectCateringItemsToRestore);
  
  // Check if this is a modify order mode from Redux
  const isModifyOrderMode = useSelector(selectIsModifyOrderMode);
  const modifyOrderPax = useSelector(selectModifyOrderPax);

  // Additional services state
  const additionalServices = useSelector(selectAdditionalServices);
  const selectedAdditionalService = useSelector(selectSelectedAdditionalService);
  const additionalServiceItems = useSelector(selectAdditionalServiceItems);
  const additionalServiceCategories = useSelector(selectAdditionalServiceCategories);
  const additionalServiceFilters = useSelector(selectCateringAdditionalServiceFilters);
  
  // Debug log for additional service state changes
  useEffect(() => {
    console.log('🔍 Redux State Changed:', {
      selectedAdditionalService: selectedAdditionalService?.name_en,
      selectedAdditionalServiceExists: !!selectedAdditionalService,
      additionalServiceItems: additionalServiceItems?.length,
      additionalServiceItemsExists: !!additionalServiceItems,
      additionalServiceItemsIsArray: Array.isArray(additionalServiceItems),
      additionalServiceCategories: additionalServiceCategories?.length,
      additionalServiceCategoriesExists: !!additionalServiceCategories,
      additionalServiceCategoriesIsArray: Array.isArray(additionalServiceCategories),
      shouldRenderMenu: !!(selectedAdditionalService && additionalServiceItems && additionalServiceCategories),
      fullCheck: {
        service: !!selectedAdditionalService,
        items: !!additionalServiceItems,
        categories: !!additionalServiceCategories
      }
    });
  }, [selectedAdditionalService, additionalServiceItems, additionalServiceCategories]);

  const [loading, setLoading] = useState(true);
  const [packageDetail, setPackageDetail] = useState(null);
  const [additionalServiceDetail, setAdditionalServiceDetail] = useState(null);
  
  // Cache duration: 2 hours in milliseconds
  const CACHE_DURATION = 2 * 60 * 60 * 1000;
  
  // Find the selected package from existing catering packages data
  const selectedPackage = cateringPackages?.find(pkg => pkg.id === parseInt(packageId));
  console.log(selectedPackage,"selectedPackages");
  
  // Get current restaurant ID from selected package first, then fall back to cateringRestaurantId or cartRestaurantDetails
  // This ensures we use the correct restaurant for the package, especially when coming from past orders
  const restaurantId = selectedPackage?.restaurant_id || cateringRestaurantId || cartRestaurantDetails?.restaurantId || 1;
  console.log("Using restaurant ID:", restaurantId, "from selectedPackage:", selectedPackage?.restaurant_id, "cateringRestaurantId:", cateringRestaurantId);

  // Create complete package data by merging selectedPackage with packageDetail
  const completePackageData = useMemo(() => {
    if (!selectedPackage) return null;
    if (!packageDetail) return selectedPackage;
    
    return {
      ...selectedPackage,
      ...packageDetail, // This includes complete unavailability_schedule and other detailed info
      id: selectedPackage.id // Ensure ID stays from selectedPackage
    };
  }, [selectedPackage, packageDetail]);

  useEffect(() => {
    const fetchCateringData = async () => {
      if (!packageId) {
        console.error("No package ID provided");
        navigate('/catering-packages');
        return;
      }

      // Modify order mode flags are managed in Redux, no need to clear session storage
      
      // Clear any previously selected additional service when loading a new package
      // This prevents auto-selection of additional services from previous views
      console.log("Clearing previous additional service selection on mount");
      dispatch(setSelectedAdditionalService(null));
      dispatch(setAdditionalServiceItems([]));
      dispatch(setAdditionalServiceCategories([]));

      try {
        setLoading(true);
        
        // Calculate restaurantId inside effect to avoid circular dependency
        // Look up the package to get its restaurant ID, with fallbacks
        const currentSelectedPackage = cateringPackages?.find(pkg => pkg.id === parseInt(packageId));
        const effectRestaurantId = currentSelectedPackage?.restaurant_id || cateringRestaurantId || cartRestaurantDetails?.restaurantId || 1;
        
        console.log("Fetching catering package detail for:", { restaurantId: effectRestaurantId, packageId });

        // OPTIMIZATION: Only skip API calls if we're actively restoring from a past order
        // Check if itemsToRestore exists - this indicates we're in the middle of restoration
        const isRestoringPastOrder = itemsToRestore && itemsToRestore.length > 0;
        const hasPackageInCart = currentSelectedPackage && currentSelectedPackage.items && currentSelectedPackage.items.length > 0;
        
        if (isRestoringPastOrder && hasPackageInCart) {
          console.log("✅ Currently restoring past order, skipping API calls to avoid interference");
          console.log("  - Items to restore:", itemsToRestore.length);
          console.log("  - Package items in cart:", currentSelectedPackage.items.length);
          
          // Use the selected package data as packageDetail (it already has all the info)
          setPackageDetail(currentSelectedPackage);
          
          // Check for additional services from the package data
          if (currentSelectedPackage.additional_packages && Array.isArray(currentSelectedPackage.additional_packages)) {
            console.log("✅ Additional packages found in cached data:", currentSelectedPackage.additional_packages.length);
            dispatch(setAdditionalServices(currentSelectedPackage.additional_packages));
          }
          
          setLoading(false);
          return; // Skip all API calls during restoration
        }

        // Check cache validity for catering packages list
        const now = Date.now();
        const isCacheValid = cateringPackagesTimestamp && (now - cateringPackagesTimestamp < CACHE_DURATION);
        const hasCachedPackages = cateringPackages && Array.isArray(cateringPackages) && cateringPackages.length > 0;
        
        // Check if we need to fetch the packages list (for navigation, etc.)
        const shouldFetchPackagesList = !hasCachedPackages || !isCacheValid;

        const promises = [];

        // Fetch packages list if needed (to ensure we have context for navigation)
        if (shouldFetchPackagesList) {
          if (!hasCachedPackages) {
            console.log("🔄 No cached packages, fetching catering packages list...");
          } else if (!isCacheValid) {
            const cacheAge = Math.round((now - cateringPackagesTimestamp) / 1000 / 60);
            console.log(`🔄 Package cache expired (${cacheAge} minutes old), refetching...`);
          }
          promises.push(
            dispatch(getCateringPackagesAsync(effectRestaurantId, "", "en", 3))
          );
        } else {
          const cacheAge = Math.round((now - cateringPackagesTimestamp) / 1000 / 60);
          console.log(`✅ Using cached packages list (${cacheAge} minutes old)`);
        }

        // Always fetch restaurant categories for the new package
        // Categories are package-specific, not just restaurant-specific
        console.log("🔄 Fetching restaurant categories for package:", packageId);
        promises.push(
          dispatch(getRestaurantItemsCategoriesAsync(effectRestaurantId, "en", packageId))
        );

        // Always fetch package detail for the new package
        // Package details (items, categories, etc.) are package-specific
        console.log("🔄 Fetching package detail for package:", packageId);
        promises.push(
          dispatch(getCateringPackageDetailAsync(effectRestaurantId, packageId, "", "en", 3))
            .then(response => {
              console.log("Fresh package detail received:", response);
              setPackageDetail(response);
              
              // Store additional packages (additional services) if available
              // The field is called 'additional_packages' in the API
              if (response && response.additional_packages && Array.isArray(response.additional_packages)) {
                console.log("✅ Additional packages found:", response.additional_packages);
                dispatch(setAdditionalServices(response.additional_packages));
              } else {
                console.log("❌ No additional packages found in response");
                dispatch(setAdditionalServices(null));
              }
            })
        );

        // Wait for all API calls to complete
        if (promises.length > 0) {
          await Promise.all(promises);
        }

        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch catering package data:", error);
        setLoading(false);
      }
    };

    fetchCateringData();
    // Only re-run when packageId changes - removed selectedPackage and restaurantId from dependencies
    // to prevent infinite loops caused by state updates within this effect
    // restaurantId is calculated inside the effect from the latest state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, packageId, navigate]);

  // Log package data when it changes
  useEffect(() => {
    if (packageDetail) {
      console.log("Package detail loaded:", packageDetail);
    }
  }, [packageDetail]);

  // Log selected package data
  useEffect(() => {
    if (selectedPackage) {
      console.log("Selected package:", selectedPackage);
    }
  }, [selectedPackage]);

  // Auto-add package to cart with complete details when package detail is loaded
  // IMPORTANT: This should only run ONCE when first loading a package, not repeatedly
  const [hasInitiallyAddedPackage, setHasInitiallyAddedPackage] = React.useState(false);
  
  useEffect(() => {
    if (selectedPackage && packageDetail && !hasInitiallyAddedPackage) {
      const currentPackageId = parseInt(packageId);
      const isAlreadyInCart = cateringCartPackages.some(pkg => pkg.id === currentPackageId);
      
      if (!isAlreadyInCart) {
        // Merge selectedPackage with packageDetail to get complete package info
        const completePackage = {
          ...selectedPackage,
          ...packageDetail, // This includes pax_limit and other detailed info
          id: selectedPackage.id // Ensure ID stays from selectedPackage
        };
        
        console.log("Auto-adding complete package to cart (first time only):", completePackage);
        dispatch(addCateringCartPackage(completePackage));
        setHasInitiallyAddedPackage(true);
      } else {
        // Package already in cart (e.g., from restoration), mark as initialized
        setHasInitiallyAddedPackage(true);
      }
      
      // Initialize PAX to package's minimum if current PAX is below minimum
      // This ensures PAX starts at the correct minimum for this package
      const minPax = packageDetail.min_pax ? parseInt(packageDetail.min_pax) : 10;
      const maxPax = packageDetail.max_pax ? parseInt(packageDetail.max_pax) : 100;
      const currentPax = filters.pax || 20;
      
      if (currentPax < minPax || currentPax > maxPax) {
        console.log(`🎯 Package Detail: Initializing PAX from ${currentPax} to minPax: ${minPax}`);
        dispatch(setCateringCartFilters({ ...filters, pax: minPax }));
      }
    }
  }, [selectedPackage, packageDetail, packageId, cateringCartPackages, dispatch, filters, hasInitiallyAddedPackage]);
  
  // Reset the flag when packageId changes (navigating to a different package)
  useEffect(() => {
    setHasInitiallyAddedPackage(false);
  }, [packageId]);

  useEffect(() => {
    if (cateringCategories) {
      console.log("Categories loaded:", cateringCategories);
      console.log("Categories count:", cateringCategories.length);
      console.log("Category names:", cateringCategories.map(cat => ({
        id: cat.id,
        name_en: cat.name_en,
        name_ar: cat.name_ar,
        status: cat.status,
        items_count: cat.items_count
      })));
    }
  }, [cateringCategories]);

  useEffect(() => {
    if (cateringItems && itemsToRestore && Array.isArray(itemsToRestore) && itemsToRestore.length > 0) {

      
      try {
        // Find the package in cart (it should already be there from navigation)
        const currentPackageId = parseInt(packageId);
        const existingPackage = cateringCartPackages.find(pkg => pkg.id === currentPackageId);
        
        if (existingPackage) {

          
          // ⚠️ IMPORTANT: Prevent running restoration twice
          // If package already has items, it means restoration has already been done
          if (existingPackage.items && existingPackage.items.length > 0) {

            // Clear itemsToRestore to prevent future runs
            dispatch(clearCateringItemsToRestore());
            return;
          }
          
          // Group items by category to properly reconstruct addOrderHistory
          const itemsByCategory = {};
          itemsToRestore.forEach(itemToRestore => {
            const categoryId = itemToRestore.category_id;
            if (!itemsByCategory[categoryId]) {
              itemsByCategory[categoryId] = [];
            }
            // Store items with their add_on_type to distinguish half/full/normal variations
            itemsByCategory[categoryId].push(itemToRestore);
          });
          
          // Get the current PAX to calculate item prices
          const currentPax = filters?.pax || 20;
          
          // Process each category in order
          Object.keys(itemsByCategory).sort((a, b) => parseInt(a) - parseInt(b)).forEach(categoryId => {
            const categoryItems = itemsByCategory[categoryId];
            
            // Find the category limit to determine how many free items are allowed
            const categoryLimit = existingPackage.categories?.find(cat => cat.id === parseInt(categoryId));
            let maxFreeItems = 0;
            
            if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
              const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
              const matchingRule = sortedRules.find(rule => currentPax >= parseInt(rule.pax));
              
              if (matchingRule && matchingRule.choose > 0) {
                maxFreeItems = matchingRule.choose;
              }
            }
            
            console.log(`🔄 Category ${categoryId}: Max free items = ${maxFreeItems}`);
            
            // Calculate total instances in category
            const totalCategoryInstances = categoryItems.reduce((sum, item) => sum + item.quantity, 0);
            const totalChargeableInCategory = Math.max(0, totalCategoryInstances - maxFreeItems);

            
            // Build a flat list of all item instances with their metadata
            // Key insight: Free instances come first (up to maxFreeItems), then chargeable ones
            // Within free/chargeable groups, items appear in API order
            
            // First pass: collect metadata about each item
            const itemMetadata = [];
            categoryItems.forEach((itemToRestore) => {
              const fullItem = cateringItems.find(item => item.id === itemToRestore.menu_id);
              
              if (fullItem) {
                const addOnType = itemToRestore.add_on_type || "";
                
                // Calculate unit price based on add_on_type
                let unitPrice = 0;
                if (fullItem.add_on_options && Array.isArray(fullItem.add_on_options) && fullItem.add_on_options.length > 0) {
                  // Check for new add_on_type structure (half/full)
                  if (fullItem.add_on_options[0].add_on_type !== undefined) {
                    if (addOnType) {
                      // Find price for specific add_on_type (half or full)
                      const matchingOption = fullItem.add_on_options.find(option => option.add_on_type === addOnType);
                      unitPrice = matchingOption && matchingOption.price ? parseFloat(matchingOption.price) : 0;
                    }
                    // else: normal item with add_on_options but no add_on_type selected = 0 price
                  } else {
                    // Old structure with pax_from/pax_to
                    const matchingOption = fullItem.add_on_options.find(option => {
                      const paxFrom = parseInt(option.pax_from || 0);
                      const paxTo = parseInt(option.pax_to || 999);
                      return currentPax >= paxFrom && currentPax <= paxTo;
                    });
                    unitPrice = matchingOption && matchingOption.price ? parseFloat(matchingOption.price) : 0;
                  }
                }
                
                // Determine how many of this item's instances are chargeable
                // IMPORTANT: Count complimentary instances from itemToRestore.complimentaryCount
                const complimentaryCount = itemToRestore.complimentaryCount || 0;
                const addOnPrice = itemToRestore.add_on_price || 0;
                let chargeableForThisItem = 0;
                
                if (complimentaryCount > 0) {
                  // Some or all instances are complimentary
                  // Calculate chargeable instances: total quantity minus complimentary count
                  // But also consider add_on_price to determine actual chargeable count
                  if (unitPrice > 0 && addOnPrice > 0) {
                    // Calculate chargeable from add_on_price, but subtract complimentary count
                    const chargeableFromPrice = Math.round(addOnPrice / unitPrice);
                    chargeableForThisItem = Math.max(0, chargeableFromPrice - complimentaryCount);
                  } else {
                    // No add_on_price means all non-complimentary instances are free (within category limit)
                    chargeableForThisItem = 0;
                  }
                  console.log(`🔄 Item ${fullItem.name_en}: ${itemToRestore.quantity} total, ${complimentaryCount} complimentary, ${chargeableForThisItem} chargeable`);
                } else if (unitPrice > 0 && addOnPrice > 0) {
                  chargeableForThisItem = Math.round(addOnPrice / unitPrice);
                }
                
                const freeForThisItem = itemToRestore.quantity - chargeableForThisItem;
                
                console.log(`🔄 Item ${fullItem.name_en}${addOnType ? ` (${addOnType})` : ''}: ${itemToRestore.quantity} total (${freeForThisItem} free, ${chargeableForThisItem} chargeable)`);
                
                itemMetadata.push({
                  itemId: itemToRestore.menu_id,
                  itemName: fullItem.name_en,
                  addOnType: addOnType,
                  totalQuantity: itemToRestore.quantity,
                  freeInstances: freeForThisItem,
                  chargeableInstances: chargeableForThisItem,
                  unitPrice: unitPrice,
                  complimentaryCount: complimentaryCount // Preserve complimentary count
                });
              }
            });
            
            // Second pass: build allInstances array with proper ordering
            // All free instances come first (in item order), then all chargeable instances (in item order)
            const allInstances = [];
            let globalInstanceCounter = 0;
            
            // Add all free instances first
            itemMetadata.forEach(meta => {
              for (let i = 0; i < meta.freeInstances; i++) {
                allInstances.push({
                  itemId: meta.itemId,
                  itemName: meta.itemName,
                  addOnType: meta.addOnType,
                  isChargeable: false,
                  globalOrder: globalInstanceCounter++,
                  unitPrice: meta.unitPrice
                });
              }
            });
            
            // Then add all chargeable instances
            itemMetadata.forEach(meta => {
              for (let i = 0; i < meta.chargeableInstances; i++) {
                allInstances.push({
                  itemId: meta.itemId,
                  itemName: meta.itemName,
                  addOnType: meta.addOnType,
                  isChargeable: true,
                  globalOrder: globalInstanceCounter++,
                  unitPrice: meta.unitPrice
                });
              }
            });
            
            console.log(`🔄 All instances in category:`, allInstances);
            
            // Now rebuild items with correct addOrderHistory based on when their instances appear
            console.log(`🔄 Processing ${categoryItems.length} items for category ${categoryId}`);
            categoryItems.forEach((itemToRestore, itemIndex) => {
              console.log(`🔄 [${itemIndex + 1}/${categoryItems.length}] Looking for menu_id: ${itemToRestore.menu_id}`);
              const fullItem = cateringItems.find(item => item.id === itemToRestore.menu_id);
              
              if (fullItem) {
                const addOnType = itemToRestore.add_on_type || "";
                
                // Find all instances of this item AND add_on_type in the global order
                const itemInstances = allInstances.filter(inst => 
                  inst.itemId === itemToRestore.menu_id && 
                  (inst.addOnType || "") === addOnType
                );
                const addOrderHistory = itemInstances.map(inst => inst.globalOrder);
                

                
                // Create the item object with reconstructed addOrderHistory
                const itemToAdd = {
                  ...fullItem,
                  quantity: itemToRestore.quantity,
                  category_id: itemToRestore.category_id || fullItem.category_id,
                  addOrderHistory: addOrderHistory
                };
                
                // Set complimentaryCount from itemToRestore (already calculated during grouping)
                // Find the metadata for this item to get the complimentary count
                const itemMeta = itemMetadata.find(meta => 
                  meta.itemId === itemToRestore.menu_id && 
                  (meta.addOnType || "") === addOnType
                );
                if (itemMeta && itemMeta.complimentaryCount > 0) {
                  // Set complimentaryCount from the grouped data
                  itemToAdd.complimentaryCount = itemMeta.complimentaryCount;
                  console.log(`🔄 Setting complimentaryCount = ${itemMeta.complimentaryCount} for ${fullItem.name_en} (total quantity: ${itemToRestore.quantity})`);
                } else if (itemToRestore.complimentaryCount > 0) {
                  // Fallback: use complimentaryCount directly from itemToRestore
                  itemToAdd.complimentaryCount = itemToRestore.complimentaryCount;
                  console.log(`🔄 Setting complimentaryCount = ${itemToRestore.complimentaryCount} for ${fullItem.name_en} (from itemToRestore)`);
                }
                
                // Include add_on_type if present (for half/full variations)
                if (addOnType) {
                  itemToAdd.add_on_type = addOnType;
                  // Calculate and set the price based on add_on_type
                  if (fullItem.add_on_options && Array.isArray(fullItem.add_on_options) && fullItem.add_on_options.length > 0) {
                    if (fullItem.add_on_options[0].add_on_type !== undefined) {
                      const matchingOption = fullItem.add_on_options.find(option => option.add_on_type === addOnType);
                      if (matchingOption && matchingOption.price) {
                        itemToAdd.price = `KWD ${parseFloat(matchingOption.price).toFixed(3)}`;
                      }
                    }
                  }
                }
                
                // Add the item with the correct addOrderHistory
                console.log(`🔄   Dispatching addItemToCateringPackage for ${fullItem.name_en} with quantity ${itemToAdd.quantity}`);
                dispatch(addItemToCateringPackage({
                  packageId: currentPackageId,
                  item: itemToAdd,
                  packageMeta: {
                    title: existingPackage.name_en || existingPackage.title,
                    basePrice: existingPackage.basePrice || 0
                  }
                }));
                console.log(`🔄   ✅ Item added to package`);
              } else {
                console.warn(`🔄 ❌ Could not find full item data for menu_id: ${itemToRestore.menu_id}`);
                console.warn(`🔄    Available menu IDs: ${cateringItems.map(i => i.id).join(', ')}`);
              }
            });
          });
          
          console.log("🔄 ✅ Item restoration complete, clearing itemsToRestore from Redux");
          // Note: Check the cart state in the next render cycle to see the final result
          // Clear the items to restore from Redux after processing
          dispatch(clearCateringItemsToRestore());
        } else {
          console.warn('🔄 ⚠️ Package not found in cart for restoration');
        }
      } catch (error) {
        console.error('🔄 ❌ Error restoring items from past order:', error);
        // Clear the Redux state even on error
        dispatch(clearCateringItemsToRestore());
      }
    }
  }, [cateringItems, itemsToRestore, packageId, cateringCartPackages, dispatch, filters]);

  // Track if we've already attempted auto-selection (to prevent loops)
  const hasAttemptedAutoSelect = React.useRef(false);
  
  // Auto-select first additional service package in cart on mount
  useEffect(() => {
    // Only auto-select once and if there's no currently selected additional service
    if (hasAttemptedAutoSelect.current) {
      return;
    }
    
    if (!selectedAdditionalService && additionalServices && additionalServices.length > 0 && cateringCartPackages) {
      // Find the first additional service package that's in the cart (with items)
      const firstAdditionalServiceInCart = additionalServices.find(pkg => {
        const cartPkg = cateringCartPackages.find(cp => cp.id === pkg.id && cp.isAdditionalService);
        return cartPkg && cartPkg.items && cartPkg.items.length > 0; // Only auto-select if it has items
      });
      
      if (firstAdditionalServiceInCart) {
        console.log('📦 Auto-selecting first additional service in cart:', firstAdditionalServiceInCart.name_en);
        hasAttemptedAutoSelect.current = true;
        
        // Use async IIFE to call the handler
        (async () => {
          await handleAdditionalServiceSelect(firstAdditionalServiceInCart);
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalServices, cateringCartPackages, selectedAdditionalService]);

  // Track which packages have had items added (to distinguish empty initialization from cleared cart)
  const [packagesWithItemsHistory, setPackagesWithItemsHistory] = useState(new Set());
  
  // Update history when packages get items (use ref to check if anything changed)
  const prevCartPackagesRef = React.useRef(null);
  useEffect(() => {
    if (cateringCartPackages) {
      // Create a snapshot of package IDs with items
      const currentSnapshot = cateringCartPackages
        .filter(pkg => pkg.isAdditionalService && pkg.items && pkg.items.length > 0)
        .map(pkg => pkg.id)
        .sort()
        .join(',');
      
      // Only update if something actually changed
      if (prevCartPackagesRef.current !== currentSnapshot) {
        prevCartPackagesRef.current = currentSnapshot;
        
        setPackagesWithItemsHistory(prev => {
          const newSet = new Set(prev);
          let hasChanges = false;
          
          cateringCartPackages.forEach(pkg => {
            if (pkg.isAdditionalService && pkg.items && pkg.items.length > 0) {
              if (!newSet.has(pkg.id)) {
                newSet.add(pkg.id);
                hasChanges = true;
              }
            }
          });
          
          // Only return new set if there were actual changes
          return hasChanges ? newSet : prev;
        });
      }
    }
  }, [cateringCartPackages]);
  
  // Clear selected additional service ONLY if it was removed from cart entirely
  // Don't clear just because it has no items (it might be newly initialized)
  useEffect(() => {
    if (!selectedAdditionalService || !cateringCartPackages) {
      return;
    }
    
    // Check if selected additional service is in cart
    const isInCart = cateringCartPackages.some(
      cartPkg => cartPkg.id === selectedAdditionalService.id && cartPkg.isAdditionalService
    );
    
    // Only clear if package is NOT in cart at all (completely removed)
    // AND it previously had items (to avoid clearing during initialization)
    if (!isInCart && packagesWithItemsHistory.has(selectedAdditionalService.id)) {
      console.log('🗑️ Selected additional service removed from cart, clearing selection:', selectedAdditionalService.name_en);
      
      // Use a ref to prevent multiple dispatches
      const packageIdToRemove = selectedAdditionalService.id;
      
      dispatch(setSelectedAdditionalService(null));
      dispatch(setAdditionalServiceItems([]));
      dispatch(setAdditionalServiceCategories([]));
      
      // Remove from history
      setPackagesWithItemsHistory(prev => {
        if (prev.has(packageIdToRemove)) {
          const newSet = new Set(prev);
          newSet.delete(packageIdToRemove);
          return newSet;
        }
        return prev;
      });
    }
  }, [cateringCartPackages, selectedAdditionalService, dispatch, packagesWithItemsHistory]);

  // Handle additional service package selection
  const handleAdditionalServiceSelect = async (additionalPackage) => {
    try {
      console.log("🎯 Additional service selected:", additionalPackage);
      console.log("🎯 Dispatching setSelectedAdditionalService with:", additionalPackage);
      dispatch(setSelectedAdditionalService(additionalPackage));
      console.log("🎯 ✅ setSelectedAdditionalService dispatched");
      
      // Verify it was set (React will re-render, but log synchronously)
      setTimeout(() => {
        console.log("🎯 Verification: selectedAdditionalService should be set now");
      }, 100);
      
      // Initialize PAX for additional service
      const minPax = additionalPackage.min_pax ? parseInt(additionalPackage.min_pax) : 10;
      dispatch(setCateringAdditionalServiceFilters({ 
        pax: minPax, 
        search: "" 
      }));
      
      // Check if package is already in cart
      const isAlreadyInCart = cateringCartPackages.some(pkg => pkg.id === additionalPackage.id);
      
      // Fetch categories for additional service
      // Pass skipReduxUpdate=true to prevent overwriting main package categories
      console.log("🎯 Fetching categories for additional service package:", additionalPackage.id);
      console.log("🎯 Using restaurantId:", restaurantId);
      let categoriesResponse;
      try {
        console.log("🎯 About to dispatch categories thunk...");
        categoriesResponse = await dispatch(getRestaurantItemsCategoriesAsync(restaurantId, "en", additionalPackage.id, true));
        console.log("🎯 ✅ Categories dispatch completed, response:", categoriesResponse);
        console.log("🎯 Additional service categories from API:", categoriesResponse);
        console.log("🎯 Categories response type:", typeof categoriesResponse);
        console.log("🎯 Categories is array:", Array.isArray(categoriesResponse));
      } catch (catError) {
        console.error("🎯 ❌ Error fetching categories:", catError);
        throw catError;
      }
      
      // Store categories separately for additional service
      if (categoriesResponse && Array.isArray(categoriesResponse)) {
        console.log("🎯 Setting additional service categories to Redux:", categoriesResponse.length);
        dispatch(setAdditionalServiceCategories(categoriesResponse));
        console.log("🎯 ✅ Additional service categories dispatched");
      } else {
        console.error("🎯 ❌ Categories response is not an array:", categoriesResponse);
      }
      
      // Fetch package detail for additional service
      // IMPORTANT: Pass skipReduxUpdate=true to prevent overwriting main package data
      const detailResponse = await dispatch(getCateringPackageDetailAsync(restaurantId, additionalPackage.id, "", "en", 3, true));
      setAdditionalServiceDetail(detailResponse);
      
      // IMPORTANT: Add/Update package in cart with full details including pricing_tiers
      if (detailResponse) {
        const packageInCart = cateringCartPackages.find(pkg => pkg.id === additionalPackage.id);
        
        const packageToAddOrUpdate = {
          ...(packageInCart || additionalPackage), // Start with existing cart package or original package
          ...detailResponse, // Merge in full detail including pricing_tiers
          id: additionalPackage.id, // Preserve original ID
          isAdditionalService: true,
          restaurant_id: restaurantId,
          parent_restaurant_id: cateringRestaurantId,
          items: packageInCart?.items || [], // Preserve existing items or start with empty
          pax: packageInCart?.pax || minPax // Preserve existing PAX or use minPax
        };
        
        dispatch(addCateringCartPackage(packageToAddOrUpdate));
      }
      
      // Extract items from package detail response
      // This keeps them separate from main package data
      if (detailResponse && detailResponse.categories) {
        console.log("🎯 Extracting items from categories...");
        // Extract items from categories (same format as the thunk does for main package)
        const allItems = detailResponse.categories.flatMap(category => {
          console.log(`🎯 Category: ${category.name_en}, items:`, category.catering_service_item?.length || 0);
          return (category.catering_service_item || []).map(item => ({
            ...item,
            category_id: category.id,
            category_name_en: category.name_en,
            category_name_ar: category.name_ar
          }));
        });
        console.log("🎯 Setting additional service items to Redux:", allItems.length);
        dispatch(setAdditionalServiceItems(allItems));
        console.log("🎯 ✅ Additional service items dispatched");
        console.log("🎯 Sample items:", allItems.slice(0, 3).map(i => ({ id: i.id, name: i.name_en, category: i.category_name_en })));
      } else {
        console.error("🎯 ❌ No categories in detail response");
      }
      
      // Store package detail for later use when items are added
      // IMPORTANT: Don't auto-add to cart here - only add when user adds an item
      setAdditionalServiceDetail(detailResponse);
    } catch (error) {
      console.error("🎯 ❌ Error loading additional service:", error);
      alert("Failed to load additional service. Please try again.");
    }
  };

  // Helper function to format price with KWD currency prefix
  const formatPrice = (price) => {
    if (typeof price === "string" && price.startsWith("KWD ")) {
      return price;
    }
    const numericPrice = parseFloat(price);
    return `KWD ${numericPrice.toFixed(3)}`;
  };

  // Helper function to calculate package price based on pax
  const calculatePackagePrice = (packageData, pax) => {
    if (!packageData || !pax || pax <= 0) return 0;

    // Check if package has new pricing_tiers structure
    if (packageData.pricing_tiers && Array.isArray(packageData.pricing_tiers)) {
      // Sort tiers by PAX in descending order to find the highest applicable tier
      const sortedTiers = [...packageData.pricing_tiers].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
      
      // Find the appropriate pricing tier for current PAX
      const matchingTier = sortedTiers.find(tier => 
        pax >= parseInt(tier.pax)
      );

      if (matchingTier && matchingTier.price) {
        return parseFloat(matchingTier.price);
      }

      // If no exact match, use the lowest tier (fallback to first tier)
      if (packageData.pricing_tiers.length > 0) {
        return parseFloat(packageData.pricing_tiers[0].price);
      }
    }

    // Fallback to old structure for backward compatibility
    const paxRanges = [
      {
        from: packageData.pax_range_from || 0,
        to: packageData.pax_range_to || 0,
        price: packageData.pax_price || "KWD 0.000"
      },
      {
        from: packageData.pax_range_from2 || 0,
        to: packageData.pax_range_to2 || 0,
        price: packageData.pax_price2 || "KWD 0.000"
      },
      {
        from: packageData.pax_range_from3 || 0,
        to: packageData.pax_range_to3 || 0,
        price: packageData.pax_price3 || "KWD 0.000"
      },
      {
        from: packageData.pax_range_from4 || 0,
        to: packageData.pax_range_to4 || 0,
        price: packageData.pax_price4 || "KWD 0.000"
      },
      {
        from: packageData.pax_range_from5 || 0,
        to: packageData.pax_range_to5 || 0,
        price: packageData.pax_price5 || "KWD 0.000"
      }
    ];

    // Find the matching pax range
    for (const range of paxRanges) {
      if (range.from > 0 && range.to > 0 && pax >= range.from && pax <= range.to) {
        // Extract numeric value from price string
        const priceMatch = range.price.match(/[\d.]+/);
        return priceMatch ? parseFloat(priceMatch[0]) : 0;
      }
    }

    // If no range matches, return 0 or default price
    return 0;
  };

  // Memoize props to prevent infinite re-renders
  const memoizedSelectedPackages = useMemo(() => {
    if (!completePackageData) return [];
    
    // Calculate the package price based on current pax and complete package data
    const calculatedPrice = calculatePackagePrice(completePackageData, filters.pax);
    
    // Get items from Redux catering cart packages instead of packageDetail
    const currentPackageId = parseInt(packageId);
    const cartPackage = cateringCartPackages.find(pkg => pkg.id === currentPackageId);
    const cartItems = cartPackage?.items || [];
    
    // Process items from cart with proper pricing
    const processedItems = cartItems.map(item => ({
      ...item,
      price: parseFloat(item.price?.replace(/[^0-9.]/g, '') || 0) // Extract numeric price
    }));
    
    // Use complete package data that already includes all detailed info
    const finalPackage = {
      ...completePackageData,
      title: completePackageData.name_en || completePackageData.title || 'Package', // Ensure title is available
      basePrice: calculatedPrice,
      items: processedItems, // Use cart items, not API items
    };
    
    const result = [finalPackage];
    
    console.log("Memoized selected packages:", result);
    return result;
  }, [completePackageData, filters.pax, cateringCartPackages, packageId]);


  return (
    <>
      {loading ? (
        <div className="flex justify-center items-center h-screen">
          <div className="spinner-border animate-spin border-t-4 border-b-4 border-gray-600 w-16 h-16 rounded-full"></div>
        </div>
      ) : (
        <div className="h-screen bg-white flex flex-col overflow-hidden">
          <PackageDetailFilterBar 
            selectedPackage={completePackageData} 
            isModifyOrderMode={isModifyOrderMode}
            modifyOrderPax={modifyOrderPax}
          />
          <div className="flex flex-1 flex-row overflow-hidden">
            <div className="w-3/4 flex flex-col flex-1 relative overflow-y-auto">
              {/* Main Package Section - with max height to ensure additional services are visible */}
              <div className="flex-shrink-0" style={{ maxHeight: '60vh' }}>
                <Menu 
                  branchDetails={branchDetails} 
                  isCatering={true} 
                  activePackageId={parseInt(packageId)}  
                  displayName={completePackageData?.name_en || completePackageData?.title || 'Package'}
                  disabled={!filters.time}
                  packageData={completePackageData}
                  isModifyOrderMode={isModifyOrderMode}
                />
              </div>
              {additionalServices && additionalServices.length > 0 && (
                <>
                  <AdditionalServices 
                    packages={additionalServices}
                    onSelectPackage={handleAdditionalServiceSelect}
                    selectedPackageId={selectedAdditionalService?.id}
                  />
                  
                  {/* Additional Service Menu - only show when a service is selected */}
                  {selectedAdditionalService && additionalServiceItems && additionalServiceCategories && (
                    <div className="flex-shrink-0 border-t-4 border-gray-300" style={{ maxHeight: '60vh' }}>
                      <Menu 
                        branchDetails={branchDetails} 
                        isCatering={true} 
                        activePackageId={parseInt(selectedAdditionalService.id)}  
                        displayName={selectedAdditionalService.name_en || 'Additional Service'}
                        disabled={!filters.time}
                        packageData={selectedAdditionalService}
                        isModifyOrderMode={isModifyOrderMode}
                        isAdditionalService={true}
                        additionalServiceCategories={additionalServiceCategories}
                        additionalServiceItems={additionalServiceItems}
                      />
                    </div>
                  )}
                </>
              )}
              
              {!filters.time && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-600 mb-2">Please select a time slot</h3>
                    <p className="text-gray-500">Choose a time slot above to start adding items to your order</p>
                  </div>
                </div>
              )}
              {showProductDetail && <Product />}
            </div>
            <div className="w-1/4 border-l border-gray-200 bg-[#FBFBFB] flex-shrink-0 flex flex-col overflow-hidden">
              {!showProductDetail && (
                <CateringOrderSummary 
                  selectedPackages={memoizedSelectedPackages} 
                  showPackageDetailButtons={true}
                  disabled={!filters.time}
                  isModifyOrderMode={isModifyOrderMode}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlaceCateringOrder; 